import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import ApprenantDossierBanner from './ApprenantDossierBanner';
import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import ApprenantPageShell from './ApprenantPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  fetchDossierStatut,
  fetchFinancialHistory,
  fetchMyInscriptions,
} from '../../services/academiaApprenantService';
import { fetchParrainageDashboard } from '../../services/parrainageService';
import {
  buildInscriptionPaymentNavigationState,
  inscriptionFraisInscriptionDue,
  inscriptionResteAPayer,
  toAmount,
} from '../../utils/inscriptionPresentation';
import { formatMoney } from '../../utils/currency';

const QUICK_LINKS = [
  { to: '/app/mes-cours', icon: 'list_alt', title: 'Mes cours', desc: 'Suivre vos formations' },
  { to: '/app/formations', icon: 'school', title: 'Formations', desc: 'Catalogue et cohortes' },
  { to: '/app/documents', icon: 'folder', title: 'Mon dossier', desc: 'Pièces et validations' },
  { to: '/app/notifications', icon: 'notifications', title: 'Notifications', desc: 'Alertes et annonces' },
  { to: '/app/parrainage', icon: 'groups', title: 'Parrainage', desc: 'Code et commissions' },
  { to: '/app/wallet?tab=historique', icon: 'receipt_long', title: 'Historique', desc: 'Paiements et reçus' },
  { to: '/app/profil', icon: 'person', title: 'Mon profil', desc: 'Coordonnées et photo' },
] as const;

type FinancialSummary = {
  totalFacture?: number;
  totalPaye?: number;
  resteGlobal?: number;
};

type FinancialTimelineRow = {
  id?: string;
  type?: string;
  date?: string;
  libelle?: string;
  montant?: number | string;
  filiereLibelle?: string;
  statut?: string;
};

type ParrainageSnapshot = {
  codeParrainage?: string;
  walletSolde?: number;
  walletDevise?: string;
  filleulCount?: number;
};

type InscriptionRow = {
  id?: string;
  statut?: string;
  matriculeSnapshot?: string;
  dateInscription?: string;
  montantTotalCdf?: number | string;
  montantPayeCdf?: number | string;
  filiere?: { libelle?: string; code?: string };
  cohorte?: { code?: string; libelle?: string; dateDebut?: string; dateFin?: string };
  lignesFrais?: Array<{ typeFrais?: string; montantCdf?: number | string; montantPayeCdf?: number | string }>;
};

type DossierStatut = {
  complet?: boolean;
  inscriptionEligible?: boolean;
  message?: string;
};

function formatShortDate(value?: string) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function computeInscriptionStats(items: InscriptionRow[]) {
  let actifs = 0;
  let enAttente = 0;
  for (const row of items) {
    if (row.statut === 'ANNULEE' || row.statut === 'DESISTEE') continue;
    if (
      row.statut === 'EN_ATTENTE_PAIEMENT' ||
      row.statut === 'PARTIELLEMENT_PAYEE' ||
      row.statut === 'BROUILLON'
    ) {
      enAttente += 1;
    } else {
      actifs += 1;
    }
  }
  return { total: items.length, actifs, enAttente };
}

function isPendingPayment(row: InscriptionRow) {
  return (
    inscriptionResteAPayer(row) > 0 && row.statut !== 'ANNULEE' && row.statut !== 'CONFIRMEE'
  );
}

export default function ApprenantDashboardPanel() {
  const [items, setItems] = useState<InscriptionRow[]>([]);
  const [dossierStatut, setDossierStatut] = useState<DossierStatut | null>(null);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary | null>(null);
  const [recentActivity, setRecentActivity] = useState<FinancialTimelineRow[]>([]);
  const [parrainage, setParrainage] = useState<ParrainageSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const [data, dossier, finances, parrainageData] = await Promise.all([
        fetchMyInscriptions(),
        fetchDossierStatut().catch(() => null),
        fetchFinancialHistory().catch(() => null),
        fetchParrainageDashboard().catch(() => null),
      ]);
      setItems(Array.isArray(data) ? data : []);
      setDossierStatut(dossier);
      if (finances && typeof finances === 'object') {
        setFinancialSummary((finances as { summary?: FinancialSummary }).summary ?? null);
        const timeline = (finances as { timeline?: FinancialTimelineRow[] }).timeline;
        setRecentActivity(Array.isArray(timeline) ? timeline.slice(0, 5) : []);
      } else {
        setFinancialSummary(null);
        setRecentActivity([]);
      }
      if (parrainageData && typeof parrainageData === 'object') {
        const p = parrainageData as ParrainageSnapshot & { codeParrainage: string };
        setParrainage({
          codeParrainage: p.codeParrainage,
          walletSolde: p.walletSolde,
          walletDevise: p.walletDevise,
          filleulCount: p.filleulCount,
        });
      } else {
        setParrainage(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger votre espace.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useRegisterPortalRefresh(() => void load());

  const inscriptionStats = useMemo(() => computeInscriptionStats(items), [items]);

  const pendingPayment = useMemo(() => items.filter(isPendingPayment).length, [items]);

  const pendingPaymentRows = useMemo(() => items.filter(isPendingPayment).slice(0, 3), [items]);

  const upcomingSessions = useMemo(() => {
    const now = Date.now();
    return items
      .filter((row) => {
        const start = row.cohorte?.dateDebut;
        if (!start) return false;
        const t = new Date(start).getTime();
        return !Number.isNaN(t) && t >= now;
      })
      .sort(
        (a, b) =>
          new Date(a.cohorte!.dateDebut!).getTime() - new Date(b.cohorte!.dateDebut!).getTime(),
      )
      .slice(0, 3);
  }, [items]);

  const resteGlobal =
    financialSummary?.resteGlobal != null
      ? toAmount(financialSummary.resteGlobal)
      : items.reduce((sum, row) => sum + inscriptionResteAPayer(row), 0);

  const dossierHelper =
    dossierStatut == null
      ? null
      : dossierStatut.complet
        ? 'Dossier validé — inscriptions ouvertes'
        : 'Complétez votre dossier pour vous inscrire';

  return (
    <ApprenantPageShell
      title="Mon espace apprenant"
      description="Consultez vos inscriptions, suivez les paiements et accédez à votre parcours de formation."
      sideCard={{
        label: 'Mes inscriptions',
        value: loading ? '…' : String(items.length),
        helper:
          pendingPayment > 0
            ? `${pendingPayment} paiement${pendingPayment > 1 ? 's' : ''} à finaliser`
            : dossierHelper,
        sideLinks: [
          { label: 'Mes cours', to: '/app/mes-cours', icon: 'list_alt' },
          { label: 'Parrainage', to: '/app/parrainage', icon: 'groups' },
        ],
      }}
      onRetry={() => void load()}
      isLoading={loading}
      isError={Boolean(error)}
      errorMessage={error ?? undefined}
      loadingMessage="Chargement de votre espace…"
      primaryAction={
        <Link
          to="/app/formations"
          className="inline-flex rounded-lg bg-white px-4 py-2 text-xs font-bold text-primary"
        >
          Parcourir les formations
        </Link>
      }
    >
      <ApprenantDossierBanner dossierStatut={dossierStatut} />

      <section className="apprenant-dashboard-kpi" aria-label="Indicateurs">
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--primary">
            <LucideSymbolIcon name="school" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Inscriptions actives</p>
            <p className="apprenant-dashboard-kpi__value">
              {loading ? '…' : String(inscriptionStats.actifs)}
            </p>
            <p className="apprenant-dashboard-kpi__hint">
              {inscriptionStats.total} au total
            </p>
          </div>
        </article>
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--amber">
            <LucideSymbolIcon name="payments" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Paiements à finaliser</p>
            <p className="apprenant-dashboard-kpi__value">
              {loading ? '…' : String(pendingPayment)}
            </p>
            <p className="apprenant-dashboard-kpi__hint">
              {pendingPayment > 0 ? 'Régularisez pour confirmer' : 'Aucun en attente'}
            </p>
          </div>
        </article>
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--sky">
            <LucideSymbolIcon name="account_balance_wallet" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Reste à payer</p>
            <p className="apprenant-dashboard-kpi__value apprenant-dashboard-kpi__value--sm">
              {loading ? '…' : formatMoney(resteGlobal, 'CDF')}
            </p>
            <p className="apprenant-dashboard-kpi__hint">
              {financialSummary?.totalPaye != null
                ? `${formatMoney(toAmount(financialSummary.totalPaye), 'CDF')} déjà payés`
                : 'Sur vos formations'}
            </p>
          </div>
        </article>
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--emerald">
            <LucideSymbolIcon name="groups" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Solde parrainage</p>
            <p className="apprenant-dashboard-kpi__value apprenant-dashboard-kpi__value--sm">
              {loading
                ? '…'
                : parrainage?.walletSolde != null
                  ? formatMoney(parrainage.walletSolde, parrainage.walletDevise ?? 'CDF')
                  : '—'}
            </p>
            <p className="apprenant-dashboard-kpi__hint">
              {parrainage?.filleulCount != null
                ? `${parrainage.filleulCount} filleul${parrainage.filleulCount !== 1 ? 's' : ''}`
                : 'Invitez vos proches'}
            </p>
          </div>
        </article>
      </section>

      <section aria-labelledby="apprenant-quick-links-heading">
        <h2 id="apprenant-quick-links-heading" className="apprenant-dashboard-section-title">
          Accès rapide
        </h2>
        <div className="apprenant-dashboard-quick">
          {QUICK_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className="apprenant-dashboard-quick__item">
              <span className="apprenant-dashboard-quick__icon">
                <LucideSymbolIcon name={link.icon} tone="primary" size={20} />
              </span>
              <span className="min-w-0">
                <span className="apprenant-dashboard-quick__title">{link.title}</span>
                <span className="apprenant-dashboard-quick__desc">{link.desc}</span>
              </span>
              <LucideSymbolIcon
                name="arrow_forward"
                tone="inherit"
                size={18}
                className="shrink-0 opacity-40"
              />
            </Link>
          ))}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="apprenant-dashboard-panel" aria-labelledby="apprenant-todo-heading">
          <header className="apprenant-dashboard-panel__header">
            <h2 id="apprenant-todo-heading" className="apprenant-dashboard-panel__title">
              À faire
            </h2>
            {pendingPayment > 0 ? (
              <Link to="/app/wallet?tab=historique" className="apprenant-dashboard-panel__link">
                Historique
              </Link>
            ) : null}
          </header>
          <div className="apprenant-dashboard-panel__body">
            {dossierStatut != null &&
            !(dossierStatut.inscriptionEligible ?? dossierStatut.complet) ? (
              <div className="apprenant-dashboard-alert">
                <LucideSymbolIcon name="folder" tone="primary" size={18} />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-900">Dossier incomplet</p>
                  <p className="mt-0.5 text-xs text-on-surface-variant">
                    {dossierStatut.message ?? 'Complétez votre dossier pour vous inscrire.'}
                  </p>
                </div>
                <Link to="/app/documents" className="apprenant-dashboard-alert__action">
                  Ouvrir
                </Link>
              </div>
            ) : null}
            {pendingPaymentRows.length === 0 ? (
              <p className="apprenant-dashboard-panel__empty">
                {loading ? 'Chargement…' : 'Aucune action urgente. Bon parcours !'}
              </p>
            ) : (
              <ul className="apprenant-dashboard-todo-list">
                {pendingPaymentRows.map((row) => {
                  const paymentNav = buildInscriptionPaymentNavigationState(row);
                  const fraisIns = inscriptionFraisInscriptionDue(row);
                  return (
                  <li key={row.id ?? row.matriculeSnapshot}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold text-slate-900">
                        {row.filiere?.libelle ?? 'Formation'}
                      </p>
                      <p className="text-xs text-amber-700">
                        {fraisIns > 0.01
                          ? `Frais d'inscription : ${formatMoney(fraisIns, 'CDF')}`
                          : `Reste ${formatMoney(inscriptionResteAPayer(row), 'CDF')}`}
                      </p>
                    </div>
                    {row.id && paymentNav ? (
                      <Link
                        to={`/app/inscription/${row.id}/paiement`}
                        state={paymentNav}
                        className="apprenant-dashboard-todo-list__pay"
                      >
                        {paymentNav.focusInscription ? 'Inscription' : 'Payer'}
                      </Link>
                    ) : null}
                  </li>
                  );
                })}
              </ul>
            )}
            <div className="mt-3 flex flex-wrap gap-2 border-t border-outline-variant/10 pt-3">
              <Link
                to="/app/formations"
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary"
              >
                <LucideSymbolIcon name="person_add" tone="inherit" size={14} />
                Nouvelle inscription
              </Link>
              <Link
                to="/app/formations"
                className="inline-flex items-center gap-1.5 rounded-lg border border-outline-variant/20 px-3 py-1.5 text-xs font-bold text-slate-700"
              >
                <LucideSymbolIcon name="school" tone="inherit" size={14} />
                Catalogue
              </Link>
            </div>
          </div>
        </section>

        <section className="apprenant-dashboard-panel" aria-labelledby="apprenant-sessions-heading">
          <header className="apprenant-dashboard-panel__header">
            <h2 id="apprenant-sessions-heading" className="apprenant-dashboard-panel__title">
              Prochaines sessions
            </h2>
            <Link to="/app/mes-cours" className="apprenant-dashboard-panel__link">
              Mes cours
            </Link>
          </header>
          <div className="apprenant-dashboard-panel__body">
            {upcomingSessions.length === 0 ? (
              <p className="apprenant-dashboard-panel__empty">
                {loading
                  ? 'Chargement…'
                  : items.length === 0
                    ? 'Inscrivez-vous pour voir les dates de session.'
                    : 'Aucune session à venir sur vos cohortes.'}
              </p>
            ) : (
              <ul className="apprenant-dashboard-sessions">
                {upcomingSessions.map((row) => (
                  <li key={row.id ?? row.cohorte?.code}>
                    <div className="min-w-0">
                      <p className="truncate font-bold text-slate-900">
                        {row.filiere?.libelle ?? 'Formation'}
                      </p>
                      <p className="text-xs text-on-surface-variant">
                        {row.cohorte?.libelle || row.cohorte?.code}
                        {row.cohorte?.dateDebut
                          ? ` · début ${formatShortDate(row.cohorte.dateDebut)}`
                          : ''}
                      </p>
                    </div>
                    {row.id ? (
                      <Link
                        to={`/app/inscription/${row.id}`}
                        className="shrink-0 text-xs font-bold text-primary"
                      >
                        Détail
                      </Link>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="apprenant-dashboard-panel" aria-labelledby="apprenant-activity-heading">
          <header className="apprenant-dashboard-panel__header">
            <h2 id="apprenant-activity-heading" className="apprenant-dashboard-panel__title">
              Activité récente
            </h2>
            <Link to="/app/wallet?tab=historique" className="apprenant-dashboard-panel__link">
              Tout voir
            </Link>
          </header>
          <div className="apprenant-dashboard-panel__body">
            {recentActivity.length === 0 ? (
              <p className="apprenant-dashboard-panel__empty">
                {loading ? 'Chargement…' : 'Aucune opération financière récente.'}
              </p>
            ) : (
              <ul className="apprenant-dashboard-activity">
                {recentActivity.map((row, index) => {
                  const id = String(row.id ?? row.date ?? index);
                  const isRecu = row.type === 'RECU';
                  const montant = toAmount(row.montant);
                  return (
                    <li key={`${row.type}-${id}`}>
                      <span
                        className={`apprenant-dashboard-activity__dot ${isRecu ? 'apprenant-dashboard-activity__dot--recu' : ''}`}
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {isRecu ? 'Reçu' : 'Paiement'}
                          {row.statut && !isRecu ? ` · ${row.statut}` : ''}
                        </p>
                        <p className="truncate text-xs text-on-surface-variant">
                          {row.filiereLibelle ?? formatShortDate(row.date) ?? '—'}
                        </p>
                      </div>
                      {montant > 0 ? (
                        <span className="shrink-0 text-xs font-bold text-slate-800">
                          {formatMoney(montant, 'CDF')}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <section className="apprenant-dashboard-panel" aria-labelledby="apprenant-parrainage-heading">
          <header className="apprenant-dashboard-panel__header">
            <h2 id="apprenant-parrainage-heading" className="apprenant-dashboard-panel__title">
              Parrainage
            </h2>
            <Link to="/app/parrainage" className="apprenant-dashboard-panel__link">
              Espace parrainage
            </Link>
          </header>
          <div className="apprenant-dashboard-panel__body">
            {parrainage?.codeParrainage ? (
              <div className="apprenant-dashboard-parrainage">
                <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                  Votre code
                </p>
                <p className="apprenant-dashboard-parrainage__code">{parrainage.codeParrainage}</p>
                <p className="mt-2 text-sm text-on-surface-variant">
                  Partagez ce code pour parrainer de nouveaux apprenants et cumuler des bonus.
                </p>
                <div className="mt-4 flex flex-wrap gap-3 text-sm">
                  <div>
                    <p className="text-xs text-on-surface-variant">Portefeuille</p>
                    <p className="font-bold text-slate-900">
                      {formatMoney(parrainage.walletSolde ?? 0, parrainage.walletDevise ?? 'CDF')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-on-surface-variant">Filleuls</p>
                    <p className="font-bold text-slate-900">{parrainage.filleulCount ?? 0}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="apprenant-dashboard-panel__empty">
                {loading
                  ? 'Chargement…'
                  : 'Parrainez vos proches et suivez vos commissions depuis l’espace parrainage.'}
              </p>
            )}
            <Link
              to="/app/parrainage"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-primary"
            >
              Gérer mon parrainage
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={14} />
            </Link>
          </div>
        </section>
      </div>
    </ApprenantPageShell>
  );
}
